import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../services/axios';
import { useToast } from '@chakra-ui/react';
import { useAuth } from './useAuth';

const useFollow = (artistId) => {
    const [followers, setFollowers] = useState(0);
    const [isFollowing, setIsFollowing] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast()
    const {isAuthenticated} = useAuth();



    const fetchIsFollowing = useCallback(async () => {
        // Prevent API calls with undefined artistId
        if (!artistId) {
            console.error('useFollow: artistId is undefined');
            return;
        }
        
        try {
            const response = await axiosInstance.get(`/users/artist/isFollowing`, {
                params: { artist_id: artistId },
            });
            setIsFollowing(response.data);
        }
        catch(err){
            console.error('Error fetching follow status:', err);
            toast({
                title: 'Error fetching follow status',
                description: 'Failed to fetch follow status. Please try again.',
                status: 'error',
                isClosable: true,
            })
        }
    }, [artistId, toast]);


    const fetchFollowData = useCallback(async () => {
        // Prevent API calls with undefined artistId
        if (!artistId) {
            console.error('useFollow: artistId is undefined');
            return;
        }
        
        try {
            const response = await axiosInstance.get(`/users/artist/number_of/followers`, {
                params: { artist_id: artistId },
            });                
            setFollowers(response.data);
        } catch (err) {
            console.error('Error fetching followers:', err);
            toast({
                title: 'Error fetching followers',
                description: 'Failed to fetch followers. Please try again.',
                status: 'error',
                isClosable: true,
            })
        }
    }, [artistId, toast]);


    // Fetch followers and the follow status
    useEffect(() => {
        fetchFollowData();

        if(isAuthenticated){
            fetchIsFollowing();
        }
    }, [artistId, isAuthenticated, fetchFollowData, fetchIsFollowing]);

    const followArtist = async () => {
        if (!artistId) {
            console.error('useFollow: Cannot follow artist with undefined artistId');
            return;
        }
        
        setLoading(true);
        try {
            const response = await axiosInstance.post(`/users/artist/follow?artist_id=${artistId}`);
            toast({
                title: 'Success',
                description: response.data.message,
                status: 'success',
                isClosable: true,
            })
            fetchFollowData()
            fetchIsFollowing()

        } catch (err) {
            console.error('Error following artist:', err);
            toast({
                title: 'Error following artist',
                description: err.response.data.detail,
                status: 'error',
                isClosable: true,
            })
        }
        finally {
            setLoading(false);
        }
    }


    const unfollowArtist = async () => {
        if (!artistId) {
            console.error('useFollow: Cannot unfollow artist with undefined artistId');
            return;
        }
        
        setLoading(true);
        try {
            const response = await axiosInstance.post(`/users/artist/unfollow?artist_id=${artistId}`);
            toast({
                title: 'Success',
                description: response.data.message,
                status: 'success',
                isClosable: true,
            })
            fetchFollowData()
            fetchIsFollowing()
        
        } catch (err) {
            console.error('Error following artist:', err);
            toast({
                title: 'Error following artist',
                description: err.response.data.detail,
                status: 'error',
                isClosable: true,
            })
        }
        finally {
            setLoading(false);
        }
    }
   

    // const isFollowingArtist = async (user_id) => {
    //     try {
    //         const response = await axiosInstance.get(`/users/artist/is_following/`, {
    //             params: { artist_id: artistId , user_id: user_id},
    //         });
    //         return response.data
    //     } catch (err) {
    //         console.error('Error checking follow status:', err);
    //         toast({
    //             title: 'Error checking follow status',
    //             description: 'Failed to check follow status. Please try again.',
    //             status: 'error',
    //             isClosable: true,
    //         })
    //     }
    // }



    return { followers, isFollowing, loading, followArtist, unfollowArtist };
};

export default useFollow;